import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';
import { useThemeStore } from '../../stores';
import type { ConversionTrendData } from '../../types';

interface ConversionChartProps {
  data: ConversionTrendData;
  days?: number;
}

interface ChartDataPoint {
  date: string;
  label: string;
  intakeSessions: number;
  orders: number;
}

const COLORS = {
  light: {
    intakeSessions: '#007BFF',
    orders: '#22c55e',
    grid: '#e2e8f0',
    axis: '#64748b',
    tooltipBg: '#ffffff',
    tooltipBorder: '#e2e8f0',
    tooltipText: '#0f172a',
  },
  dark: {
    intakeSessions: '#60a5fa',
    orders: '#4ade80',
    grid: '#334155',
    axis: '#94a3b8',
    tooltipBg: '#1e293b',
    tooltipBorder: '#334155',
    tooltipText: '#f1f5f9',
  },
};

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ value: number; color: string; dataKey: string }>;
  label?: string;
  isDark: boolean;
}> = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;

  const colors = isDark ? COLORS.dark : COLORS.light;

  return (
    <div
      className="rounded-lg px-4 py-3 shadow-elevated border"
      style={{
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
      }}
    >
      <p
        className="text-sm font-medium mb-2"
        style={{ color: colors.tooltipText }}
      >
        {label}
      </p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span style={{ color: colors.axis }}>
            {entry.dataKey === 'intakeSessions' ? 'Intake Sessions' : 'Orders'}:
          </span>
          <span className="font-semibold" style={{ color: colors.tooltipText }}>
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const CustomLegend: React.FC<{
  payload?: Array<{ value: string; color: string }>;
}> = ({ payload }) => {
  if (!payload?.length) return null;

  const labelMap: Record<string, string> = {
    intakeSessions: 'Intake Sessions',
    orders: 'Completed Orders',
  };

  return (
    <div className="flex items-center justify-center gap-6 mt-2">
      {payload.map((entry) => (
        <div key={entry.value} className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm text-secondary-500 dark:text-secondary-400">
            {labelMap[entry.value] || entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export const ConversionChart: React.FC<ConversionChartProps> = ({ data, days = 15 }) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const colors = isDark ? COLORS.dark : COLORS.light;

  const chartData = useMemo((): ChartDataPoint[] => {
    const today = new Date();
    const startDate = subDays(today, days - 1);
    const allDays = eachDayOfInterval({ start: startDate, end: today });

    const intakeMap = new Map<string, number>();
    data.intakeSessions.forEach((d) => {
      const key = format(parseISO(d.date), 'yyyy-MM-dd');
      intakeMap.set(key, d.count);
    });

    const orderMap = new Map<string, number>();
    data.orders.forEach((d) => {
      const key = format(parseISO(d.date), 'yyyy-MM-dd');
      orderMap.set(key, d.count);
    });

    return allDays.map((day) => {
      const key = format(day, 'yyyy-MM-dd');
      return {
        date: key,
        label: format(day, 'MMM d'),
        intakeSessions: intakeMap.get(key) || 0,
        orders: orderMap.get(key) || 0,
      };
    });
  }, [data, days]);

  return (
    <div className="w-full h-[320px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colors.grid}
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fill: colors.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: colors.grid }}
            interval="preserveStartEnd"
            tickMargin={8}
          />
          <YAxis
            tick={{ fill: colors.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tickMargin={4}
          />
          <Tooltip
            content={<CustomTooltip isDark={isDark} />}
            cursor={{ stroke: colors.grid, strokeDasharray: '4 4' }}
          />
          <Legend content={<CustomLegend />} />
          <Line
            type="monotone"
            dataKey="intakeSessions"
            stroke={colors.intakeSessions}
            strokeWidth={2.5}
            dot={false}
            activeDot={{
              r: 5,
              fill: colors.intakeSessions,
              stroke: isDark ? '#1e293b' : '#ffffff',
              strokeWidth: 2,
            }}
          />
          <Line
            type="monotone"
            dataKey="orders"
            stroke={colors.orders}
            strokeWidth={2.5}
            dot={false}
            activeDot={{
              r: 5,
              fill: colors.orders,
              stroke: isDark ? '#1e293b' : '#ffffff',
              strokeWidth: 2,
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ConversionChart;
